import type { AppResource, InventoryListing } from '@studio/shared';
import {
  CELL_LABEL,
  FACET_LABEL,
  GRAPHICS_LABEL,
  STANDING_LABEL,
  cellGraphicsOf,
  countryName,
  languageName,
  lineOf,
  listingScanOf,
  storeListingLabel,
  type Facet,
} from './model.js';

/**
 * The Inventory as a table.
 *
 * The operator exports what they see: the search and the filter live only in the
 * browser, so the file is built from the rows the page is already showing - there is
 * no second notion of "visible" anywhere. Every Store Listing on the screen is in the
 * file, and as the screen shows it: by its Market Locales where it opens into its
 * countries, by its line alone where it does not. The words are the words on the
 * screen, and the states of the graphics that are easy to confuse - never read, not
 * judged, missing under translated text - are different words, not empty cells.
 */

/** One App and one of its Store Listings: a row names both, and says which is which. */
export interface CsvListing {
  readonly app: AppResource;
  /** The App's scan is going: what the line of a Store Listing it reads says. */
  readonly appReading: boolean;
  readonly listing: InventoryListing;
}

/** The filters that were in force when the file was made. */
export interface CsvFilters {
  readonly query: string;
  readonly facet: Facet;
}

export const CSV_HEADER = [
  'App',
  'Пакет',
  'Store Listing',
  'Market Locale',
  'Состояние',
  'Время чтения',
  'Машинный перевод',
  'Графика',
  'Ссылка',
] as const;

/** Never read: there is no observation at all, and it is not an empty value. */
const NOT_READ = 'не прочитано';

/**
 * The rows of the visible set, one for every Market Locale of every visible Store Listing
 * the screen opens into its countries. One the screen does not - without a URL, or never
 * read - shows its line alone, and is one row that says what that line says: no Market
 * Locale, and no state of a cell the screen does not judge.
 */
export function csvRows(listings: readonly CsvListing[]): readonly (readonly string[])[] {
  return listings.flatMap(({ app, appReading, listing }) => {
    const identity = [app.displayName, app.packageName ?? 'пакет неизвестен', storeListingLabel(listing.storeListing)];
    const link = listing.storeListing.url ?? '';
    const { standing } = lineOf(listing);
    if (standing !== 'read') {
      // Its scan as the line tells it: empty only where the line says nothing.
      return [[...identity, '', STANDING_LABEL[standing], listingScanOf(listing, appReading) ?? '', '', '', link]];
    }
    return listing.cells.map((cell) => [
      ...identity,
      `${countryName(cell.market)} (${cell.market}) · ${languageName(cell.locale)} (${cell.locale})`,
      CELL_LABEL[cell.state],
      cell.capturedAt === null
        ? NOT_READ
        : new Date(cell.capturedAt).toLocaleString('ru-RU', { dateStyle: 'medium', timeStyle: 'short' }),
      cell.snapshotId === null ? NOT_READ : cell.machineTranslated ? 'да' : 'нет',
      GRAPHICS_LABEL[cellGraphicsOf(cell, listing.defaultLocale)],
      link,
    ]);
  });
}

/** A value a spreadsheet would read as a formula is data, and says so with an apostrophe. */
function field(value: string): string {
  const safe = /^[=+\-@]/.test(value) ? `'${value}` : value;
  return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

/**
 * The file's text: RFC 4180 with a BOM, so that a table reads the Cyrillic as Cyrillic
 * and not as mojibake.
 */
export function csvText(rows: readonly (readonly string[])[]): string {
  const records = [CSV_HEADER, ...rows].map((record) => record.map(field).join(','));
  // The BOM is what tells a table that the file is Cyrillic and not mojibake.
  return `\uFEFF${records.join('\r\n')}\r\n`;
}

/** What a file name may keep: letters, digits, dots, dashes and underscores. */
function slugOf(text: string): string {
  return text.trim().replace(/[^\p{L}\p{N}._-]+/gu, '-').replace(/^-+|-+$/g, '');
}

function dayOf(on: Date): string {
  const month = `${on.getMonth() + 1}`.padStart(2, '0');
  const day = `${on.getDate()}`.padStart(2, '0');
  return `${on.getFullYear()}-${month}-${day}`;
}

/** The file the operator gets: the rows, and a name that says what was in force. */
export function csvFile(
  listings: readonly CsvListing[],
  filters: CsvFilters,
  on: Date = new Date(),
): { readonly name: string; readonly text: string } {
  const facet = slugOf(FACET_LABEL[filters.facet]);
  const query = slugOf(filters.query);
  const name = `inventory-${facet}-${query === '' ? 'без-поиска' : query}-${dayOf(on)}.csv`;
  return { name, text: csvText(csvRows(listings)) };
}

/**
 * The browser's own download: a file the browser writes where the operator chose.
 *
 * Nothing is sent anywhere - the export is built here, from what the page already
 * holds, and handed over as a file.
 */
export function downloadCsv(name: string, text: string): void {
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  // The browser writes the file from this address; it is given up only after that.
  setTimeout(() => {
    if (typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(url);
  }, 0);
}
